import { Check, Circle, CalendarClock, Repeat } from "lucide-react";
import { buildScheduledItinerary, appointmentStatus } from "./scheduledItinerary";
import { CLINIC_TIME_ZONE } from "@/lib/clinicTime";

// =====================================================================
// ScheduledPlanItinerary — the read-first "roadmap" view for a booked
// plan (status Scheduled / In Progress). Replaces the editable planning
// board once appointments exist: a top-to-bottom timeline of booked
// appointments, done ones checked off, today highlighted, upcoming
// greyed, each visit marked done/pending. "Edit schedule" flips back to
// the board when the front desk genuinely needs to move something.
// =====================================================================

function formatPHP(centavos) {
  if (!Number.isFinite(centavos)) return "—";
  return `₱${Math.round(centavos / 100).toLocaleString()}`;
}

function fmtDateTime(iso, tz) {
  if (!iso) return "Not booked yet";
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", timeZone: tz });
    const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", timeZone: tz });
    return `${date} · ${time}`;
  } catch { return "—"; }
}

function itemLine(item) {
  const proc = item.procedure_name || item.display_label || "Procedure";
  const tooth = item.tooth_number ? `#${item.tooth_number}` : "";
  const surface = item.surface ? ` ${item.surface}` : "";
  return [proc, tooth, surface].filter(Boolean).join(" ").trim();
}

const STATUS_META = {
  done:     { label: "Done",     text: "text-emerald-700" },
  today:    { label: "Today",    text: "text-sky-700" },
  overdue:  { label: "Overdue",  text: "text-amber-700" },
  upcoming: { label: "Upcoming", text: "text-slate-400" },
};

export default function ScheduledPlanItinerary({
  plan,
  phaseNames = {},
  onEditSchedule,
  canEdit = false,
  now = null,
  timeZone = CLINIC_TIME_ZONE,
}) {
  const appointments = buildScheduledItinerary(plan);
  const nowValue = now ?? new Date().toISOString();
  const doneCount = appointments.filter((a) => a.allDone).length;
  const planTotal = Number.isFinite(Number(plan?.total_price_centavos))
    ? Number(plan.total_price_centavos)
    : (plan?.items ?? [])
        .filter((it) => it.status !== "Declined" && it.status !== "Cancelled")
        .reduce((s, it) => s + (Number(it.fee_centavos) || 0), 0);

  return (
    <div data-testid="scheduled-plan-itinerary" className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Booked itinerary</p>
        <span className="text-xs text-slate-500 tabular-nums">
          {doneCount} of {appointments.length} appointment{appointments.length === 1 ? "" : "s"} done
        </span>
      </div>

      <ol className="relative">
        {appointments.map((appt, idx) => {
          const status = appointmentStatus(appt, nowValue, timeZone);
          const meta = STATUS_META[status] || STATUS_META.upcoming;
          const isLast = idx === appointments.length - 1;
          const label = phaseNames?.[String(appt.phase)] || `Appointment ${appt.phase}`;
          const isToday = status === "today";
          return (
            <li key={appt.phase} data-testid={`itinerary-appt-${appt.phase}`} data-status={status} className="grid grid-cols-[22px_1fr] gap-2.5">
              <div className="flex flex-col items-center">
                <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                  status === "done" ? "bg-emerald-500 text-white"
                    : status === "today" ? "bg-sky-500 text-white"
                    : status === "overdue" ? "bg-amber-400 text-white"
                    : "bg-white border-[1.5px] border-slate-300"
                }`}>
                  {status === "done" ? <Check className="w-3 h-3" />
                    : status === "today" ? <span className="w-1.5 h-1.5 rounded-full bg-white" />
                    : null}
                </span>
                {!isLast ? <span className="flex-1 w-0.5 bg-slate-200 my-1" /> : null}
              </div>

              <div className={`pb-3.5 ${isToday ? "" : ""}`}>
                <div className={`${isToday ? "rounded-lg border-[1.5px] border-sky-300 bg-sky-50 px-2.5 py-2" : ""}`}>
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className={`text-[13px] font-semibold ${isToday ? "text-sky-900" : "text-slate-900"}`}>{label}</span>
                    <span className={`text-xs ${isToday ? "text-sky-700" : "text-slate-500"}`}>{fmtDateTime(appt.start, timeZone)}</span>
                    {appt.durationMin ? <span className={`text-xs ${isToday ? "text-sky-700" : "text-slate-400"}`}>· {appt.durationMin} min</span> : null}
                    <span className={`text-[11px] font-medium ${isToday ? "text-sky-700" : meta.text}`}>{meta.label}</span>
                  </div>
                  <div className="mt-1.5 flex flex-col gap-1">
                    {appt.entries.map(({ item, visit }) => {
                      const done = !!visit.completed_at;
                      const vc = Array.isArray(item.procedure_visits) ? item.procedure_visits.length : 1;
                      return (
                        <div key={visit.id} data-testid={`itinerary-visit-${visit.id}`} className={`flex items-center gap-1.5 text-[13px] ${isToday ? "text-sky-800" : "text-slate-600"}`}>
                          {done
                            ? <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                            : <Circle className="w-3 h-3 text-slate-300 shrink-0" />}
                          <span className={done ? "line-through decoration-slate-300" : ""}>{itemLine(item)}</span>
                          {vc > 1 ? (
                            <span className="inline-flex items-center gap-0.5 text-[10px] text-violet-700 bg-violet-50 px-1 py-0.5 rounded">
                              <Repeat className="w-2.5 h-2.5" /> Visit {visit.sequence} of {vc}
                            </span>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ol>

      <div className="flex items-center justify-between pt-2 border-t border-slate-100">
        <span className="text-sm font-semibold text-slate-900">Total {formatPHP(planTotal)}</span>
        {canEdit && onEditSchedule ? (
          <button
            type="button"
            onClick={onEditSchedule}
            data-testid="itinerary-edit-schedule"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 text-slate-700 bg-white hover:border-slate-400"
          >
            <CalendarClock className="w-3.5 h-3.5" /> Edit schedule
          </button>
        ) : null}
      </div>
    </div>
  );
}
